/**
 * SyncStatusBadge - Phase 5: Sync Status Indicator
 *
 * Zeigt JTL Sync Status für einen Channel an:
 * - Grün: Alle Bilder synced (external_image_id gesetzt)
 * - Gelb: Teilweise synced
 * - Grau: Nicht synced / Keine Bilder
 *
 * @accessibility WCAG 2.1 Level AA compliant
 */

import { useQuery } from '@tanstack/react-query';
import { Cloud, CloudOff, CloudUpload } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SyncStatus {
  status: 'all_synced' | 'partially_synced' | 'not_synced' | 'no_images' | 'error';
  total_images: number;
  synced_images: number;
  pending_images: number;
}

interface SyncStatusResponse {
  success: boolean;
  sync_status: SyncStatus;
}

interface SyncStatusBadgeProps {
  productId: string;
  channelId: string;
  className?: string;
}

async function getChannelSyncStatus(
  productId: string,
  channelId: string
): Promise<SyncStatusResponse> {
  const response = await fetch(
    `/api/v1/products/${productId}/channels/${channelId}/sync-status`
  );
  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Sync status konnte nicht geladen werden');
  }
  return data;
}

export function SyncStatusBadge({
  productId,
  channelId,
  className,
}: SyncStatusBadgeProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['channel-sync-status', productId, channelId],
    queryFn: () => getChannelSyncStatus(productId, channelId),
    // Refetch when user returns to tab
    refetchOnWindowFocus: true,
  });

  if (isLoading) {
    return null; // Don't show badge while loading
  }

  if (!data || !data.sync_status) {
    return null;
  }

  const { status, total_images, synced_images, pending_images } = data.sync_status;

  // No images
  if (status === 'no_images') {
    return null; // Don't show badge if no images
  }

  // All synced - green cloud
  if (status === 'all_synced') {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium',
          'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          className
        )}
        title={`Alle ${total_images} Bilder mit JTL synchronisiert`}
      >
        <Cloud className="h-3.5 w-3.5" />
        <span>Synced</span>
      </div>
    );
  }

  // Partially synced - yellow cloud
  if (status === 'partially_synced') {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium',
          'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
          className
        )}
        title={`${synced_images}/${total_images} Bilder synchronisiert, ${pending_images} ausstehend`}
      >
        <CloudUpload className="h-3.5 w-3.5" />
        <span>
          {synced_images}/{total_images}
        </span>
      </div>
    );
  }

  // Not synced - grey cloud
  if (status === 'not_synced') {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium',
          'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400',
          className
        )}
        title={`${total_images} Bild${total_images !== 1 ? 'er' : ''} noch nicht synchronisiert`}
      >
        <CloudOff className="h-3.5 w-3.5" />
        <span>Nicht synced</span>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return null; // Don't show badge on error
  }

  return null;
}
